import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "../../utils/apparifySwal";
import { adminApi } from "../../services/api";
import { Eye, Pencil, FileText, X, ExternalLink, GitCompare, Plus, RefreshCw, Trash2 } from "lucide-react";
import ShopSelect from "../../components/ShopSelect";
import { Button } from "../../components/ui";
import AdminTablePagination from "./AdminTablePagination";

const PAGE_SIZE = 10;

const DOC_LABELS = {
  dti_sec_permit: "DTI/SEC Permit",
  additional_document: "Additional Document",
  id_front: "Valid ID (Front)",
  id_back: "Valid ID (Back)",
};

/** Human-readable labels for application field keys */
const FIELD_LABELS = {
  owner_first_name: "First Name",
  owner_last_name: "Last Name",
  owner_email: "Owner Email",
  owner_phone: "Owner Phone",
  business_name: "Business Name",
  registration_number: "Registration No.",
  tax_id: "Tax ID / TIN",
  business_email: "Business Email",
  business_phone: "Business Phone",
  address_line1: "Address Line 1",
  address_line2: "Address Line 2",
  city: "City",
  province: "Province",
  postal_code: "Postal Code",
  notes: "Notes",
};

const ADMIN_ACTION_LABELS = {
  "application.request_info": "Requested More Info",
  "application.approve": "Approved",
  "application.reject": "Rejected",
};

const prettyActorName = (row = {}) => {
  const full = [row.first_name || row.admin_first_name, row.last_name || row.admin_last_name].filter(Boolean).join(" ").trim();
  if (full) return full;
  return row.actor_email || row.admin_email || "Unknown User";
};

/** Render a structured diff for a single version */
const VersionDiff = ({ changedFields, docChanges: docChangesProp, snapshot, versionDocs }) => {
  // changedFields may be: object { field: { old, new } } (new structured) or array of strings (legacy)
  const fieldDiff = useMemo(() => {
    if (!changedFields) return [];
    if (typeof changedFields === "string") {
      try { return parseChangedFields(JSON.parse(changedFields)); } catch { return []; }
    }
    return parseChangedFields(changedFields);
  }, [changedFields]);

  const docChanges = docChangesProp || [];

  const parsedSnapshot = useMemo(() => {
    if (!snapshot) return null;
    if (typeof snapshot === "string") {
      try { return JSON.parse(snapshot); } catch { return null; }
    }
    return snapshot;
  }, [snapshot]);

  if (!fieldDiff.length && !docChanges.length && !parsedSnapshot && !(versionDocs || []).length) return null;

  return (
    <div className="mt-2 space-y-2">
      {fieldDiff.length > 0 && (
        <div className="space-y-1">
          {fieldDiff.map((d, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-baseline gap-1 text-xs">
              <span className="font-medium text-gray-600 dark:text-gray-300 min-w-[120px]">
                {FIELD_LABELS[d.field] || d.field}:
              </span>
              <span className="line-through text-red-500 dark:text-red-400 break-all">{d.old || "(empty)"}</span>
              <span className="text-gray-400 dark:text-gray-500 mx-1">→</span>
              <span className="text-green-600 dark:text-green-400 break-all">{d.new || "(empty)"}</span>
            </div>
          ))}
        </div>
      )}
      {docChanges.length > 0 && (
        <div className="space-y-1 pt-1 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Document Changes</p>
          {docChanges.map((dc, i) => (
            <div key={i} className="flex items-center gap-1 text-xs">
              {dc.type === "replaced" ? (
                <>
                  <RefreshCw className="w-3 h-3 text-amber-500" />
                  <span className="text-gray-600 dark:text-gray-300">{DOC_LABELS[dc.field] || dc.field}:</span>
                  <span className="line-through text-red-500 dark:text-red-400 truncate max-w-[150px]">{dc.oldFilename}</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-green-600 dark:text-green-400 truncate max-w-[150px]">{dc.newFilename}</span>
                </>
              ) : dc.type === "deleted" ? (
                <>
                  <Trash2 className="w-3 h-3 text-red-500" />
                  <span className="text-gray-600 dark:text-gray-300">{DOC_LABELS[dc.field] || dc.field}:</span>
                  <span className="line-through text-red-500 dark:text-red-400 truncate max-w-[220px]">{dc.oldFilename}</span>
                  <span className="text-red-500 dark:text-red-400">(removed)</span>
                </>
              ) : (
                <>
                  <Plus className="w-3 h-3 text-green-500" />
                  <span className="text-gray-600 dark:text-gray-300">{DOC_LABELS[dc.field] || dc.field}:</span>
                  <span className="text-green-600 dark:text-green-400 truncate max-w-[200px]">{dc.newFilename}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {(versionDocs || []).length > 0 && (
        <div className="space-y-1 pt-1 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Files In This Version</p>
          <ul className="space-y-1">
            {versionDocs.map((doc) => (
              <li key={doc.id} className="text-xs text-gray-600 dark:text-gray-300">
                <span className="font-medium">{DOC_LABELS[doc.field_name] || doc.field_name}:</span>{" "}
                {doc.original_filename || doc.stored_filename}
              </li>
            ))}
          </ul>
        </div>
      )}

      {parsedSnapshot && (
        <details className="pt-1 border-t border-gray-100 dark:border-gray-700">
          <summary className="cursor-pointer text-xs font-semibold text-gray-500 dark:text-gray-400">
            View Full Submission Snapshot
          </summary>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(parsedSnapshot).map(([key, value]) => (
              <div key={key} className="rounded-md bg-gray-50 dark:bg-gray-800/60 p-2">
                <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">{FIELD_LABELS[key] || key}</p>
                <p className="text-xs text-gray-700 dark:text-gray-200 break-words">{String(value || "(empty)")}</p>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

/** Parse changed_fields (structured object or legacy array) into [{field, old, new}] */
function parseChangedFields(data) {
  if (Array.isArray(data)) {
    // Legacy: just field names, no old/new values
    return data.map((f) => ({ field: f, old: "", new: "(updated)" }));
  }
  if (typeof data === "object" && data !== null) {
    return Object.entries(data).map(([field, val]) => ({
      field,
      old: val?.old ?? "",
      new: val?.new ?? "(updated)",
    }));
  }
  return [];
}

/** Event type labels and colors for the audit timeline */
const EVENT_TYPE_LABELS = {
  submitted: "Application Submitted",
  resubmitted: "Application Resubmitted",
  approved: "Application Approved",
  rejected: "Application Rejected",
  info_requested: "More Info Requested",
  cancelled: "Application Cancelled",
};

const EVENT_TYPE_COLORS = {
  submitted: "bg-blue-500",
  resubmitted: "bg-indigo-500",
  approved: "bg-green-500",
  rejected: "bg-red-500",
  info_requested: "bg-amber-500",
  cancelled: "bg-gray-500",
};

const createEmptyHistoryState = () => ({ versions: [], adminLogs: [], allDocs: [], events: [] });

function formatApplicationStatus(status) {
  const normalized = String(status || "").trim();
  if (!normalized) return "Unknown";
  return normalized
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getApplicationErrorDetails(error, fallbackMessage) {
  const payload = error?.response?.data || {};
  return {
    status: error?.response?.status || error?.status || null,
    code: payload.code || error?.code || null,
    currentStatus: payload.currentStatus || null,
    message: payload.msg || payload.message || error?.message || fallbackMessage,
  };
}

const BusinessApplications = ({ onUpdate }) => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [specialFilter, setSpecialFilter] = useState(null); // 'resubmitted' | 'needs_info' | 'high_resubmit' | null
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [counts, setCounts] = useState({ byStatus: {}, resubmitted: 0, needsInfo: 0, total: 0 });
  const [page, setPage] = useState(1);

  const [selectedApp, setSelectedApp] = useState(null);       // full detail (includes .documents[])
  const [detailLoading, setDetailLoading] = useState(false);
  const [history, setHistory] = useState(createEmptyHistoryState);

  // document preview state
  const [docPreview, setDocPreview] = useState(null);

  const fetchCounts = useCallback(async () => {
    try {
      const resp = await adminApi.getApplicationCounts();
      setCounts(resp.data || resp || { byStatus: {}, resubmitted: 0, needsInfo: 0, total: 0 });
    } catch { /* silent */ }
  }, []);

  const fetchPendingBusinesses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (specialFilter) params.filter = specialFilter;
      if (sortBy !== "updated_at") params.sort = sortBy;
      if (sortOrder !== "DESC") params.order = sortOrder;

      const data = await adminApi.getPendingBusinesses(params);
      setBusinesses(data.data || data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching pending businesses:", err);
      setError("Failed to load pending applications.");
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, specialFilter, statusFilter]);

  useEffect(() => {
    fetchPendingBusinesses();
    fetchCounts();
  }, [fetchCounts, fetchPendingBusinesses]);

  useEffect(() => setPage(1), [sortBy, sortOrder, specialFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(businesses.length / PAGE_SIZE));
  const visibleBusinesses = useMemo(
    () => businesses.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [businesses, page],
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const refreshApplicationInventory = useCallback(async () => {
    await Promise.all([fetchPendingBusinesses(), fetchCounts()]);
  }, [fetchCounts, fetchPendingBusinesses]);

  const openReviewModal = useCallback(async (app) => {
    setDetailLoading(true);
    setSelectedApp(app); // show skeleton immediately
    setHistory(createEmptyHistoryState());
    try {
      const [detailResp, historyResp] = await Promise.all([
        adminApi.getApplicationDetail(app.id),
        adminApi.getApplicationHistory(app.id),
      ]);
      setSelectedApp(detailResp.data || detailResp);
      const h = historyResp.data || historyResp || {};
      setHistory({
        versions: h.versions || [],
        adminLogs: h.adminLogs || [],
        allDocs: h.allDocs || [],
        events: h.events || [],
      });
    } catch (e) {
      console.error("Failed to load application detail", e);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeReviewModal = useCallback(() => {
    setSelectedApp(null);
    setDocPreview(null);
    setHistory(createEmptyHistoryState());
  }, []);

  const handleResolvedApplicationConflict = useCallback(async (app, err) => {
    const details = getApplicationErrorDetails(err, "This application has already been resolved.");

    await refreshApplicationInventory();
    if (selectedApp?.id === app.id) {
      await openReviewModal(app);
    }

    const currentStatusLabel = details.currentStatus
      ? formatApplicationStatus(details.currentStatus)
      : null;

    await Swal.fire({
      icon: "info",
      title: "Application already resolved",
      text: currentStatusLabel
        ? `This application is already ${currentStatusLabel}. The list has been refreshed to show the latest state.`
        : details.message,
    });
  }, [openReviewModal, refreshApplicationInventory, selectedApp?.id]);

  const handleRequestInfo = async (app) => {
    const appId = app.id;
    const result = await Swal.fire({
      title: "Request More Info",
      input: "textarea",
      inputLabel: "Message to applicant",
      inputPlaceholder: "Please upload a clearer permit copy and include your official registration number.",
      inputAttributes: {
        "aria-label": "Message to applicant",
      },
      showCancelButton: true,
      confirmButtonText: "Send Request",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "A message is required.";
        }
        return null;
      },
    });

    if (result.isConfirmed) {
      try {
        await adminApi.requestApplicationInfo(appId, result.value.trim());
        Swal.fire("Sent!", `Information request sent for "${app.business_name}".`, "success");
        await refreshApplicationInventory();
        if (selectedApp && selectedApp.id === appId) closeReviewModal();
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error("Error requesting more info:", err);
        const details = getApplicationErrorDetails(err, "Failed to send request. Please try again.");
        if (details.status === 409 && details.code === "APPLICATION_ALREADY_RESOLVED") {
          await handleResolvedApplicationConflict(app, err);
          return;
        }
        Swal.fire("Error!", details.message, "error");
      }
    }
  };

  const handleApprove = async (app) => {
    const appId = app.id;
    const result = await Swal.fire({
      title: "Approve Application?",
      text: `Are you sure you want to approve "${app.business_name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10B981",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, approve it!",
    });

    if (result.isConfirmed) {
      try {
        await adminApi.approveBusiness(appId);
        Swal.fire("Approved!", `${app.business_name} has been approved.`, "success");
        await refreshApplicationInventory();
        if (selectedApp && selectedApp.id === appId) closeReviewModal();
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error("Error approving application:", err);
        const details = getApplicationErrorDetails(err, "Failed to approve application. Please try again.");
        if (details.status === 409 && details.code === "APPLICATION_ALREADY_RESOLVED") {
          await handleResolvedApplicationConflict(app, err);
          return;
        }
        Swal.fire("Error!", details.message, "error");
      }
    }
  };

  const handleReject = async (app) => {
    const appId = app.id;
    const result = await Swal.fire({
      title: "Reject Application?",
      text: `Are you sure you want to reject "${app.business_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, reject it!",
      input: "textarea",
      inputLabel: "Rejection reason *",
      inputPlaceholder: "Explain why this application is being rejected.",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "A rejection reason or note is required.";
        }
        return null;
      },
    });

    if (result.isConfirmed) {
      try {
        await adminApi.rejectBusiness(appId, result.value.trim());
        Swal.fire("Rejected!", `${app.business_name} has been rejected.`, "success");
        await refreshApplicationInventory();
        if (selectedApp && selectedApp.id === appId) closeReviewModal();
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error("Error rejecting application:", err);
        const details = getApplicationErrorDetails(err, "Failed to reject application. Please try again.");
        if (details.status === 409 && details.code === "APPLICATION_ALREADY_RESOLVED") {
          await handleResolvedApplicationConflict(app, err);
          return;
        }
        Swal.fire("Error!", details.message, "error");
      }
    }
  };



  /** Build a map of version_number → doc changes from admin resubmit logs */
  const docChangesByVersion = useMemo(() => {
    const map = {};
    for (const log of history.adminLogs) {
      if (log.action_type !== "application.resubmit") continue;
      try {
        const m = typeof log.metadata === "string" ? JSON.parse(log.metadata) : log.metadata;
        if (m?.version_number && m?.document_changes) {
          map[m.version_number] = m.document_changes;
        }
      } catch { /* ignore parse errors */ }
    }
    return map;
  }, [history.adminLogs]);

  const versionDocsByVersion = useMemo(() => {
    const map = {};
    for (const doc of history.allDocs || []) {
      if (!doc.version_number) continue;
      if (!map[doc.version_number]) map[doc.version_number] = [];
      map[doc.version_number].push(doc);
    }
    return map;
  }, [history.allDocs]);

  const adminOnlyLogs = useMemo(
    () => (history.adminLogs || []).filter((log) => ["application.request_info", "application.approve", "application.reject"].includes(log.action_type)),
    [history.adminLogs]
  );

  const groupedDocuments = useMemo(() => {
    const docs = Array.isArray(selectedApp?.documents) ? selectedApp.documents : [];
    if (!docs.length) {
      return { active: [], historical: [] };
    }

    const byId = new Map(docs.map((doc) => [Number(doc.id), doc]));
    const active = [];
    const historical = [];

    for (const doc of docs) {
      const docId = Number(doc.id);
      const supersededBy = doc.superseded_by != null ? Number(doc.superseded_by) : null;

      if (!supersededBy) {
        const activeStatus = doc.replaced_document_id
          ? "Replacement"
          : doc.version_id
            ? "Added Later"
            : "Original";

        active.push({
          ...doc,
          status: activeStatus,
          statusVariant: activeStatus === "Original" ? "original" : "current",
          contextText:
            activeStatus === "Original"
              ? "Current active file"
              : "Current active file (submitted in resubmission)",
        });
        continue;
      }

      if (supersededBy === docId) {
        historical.push({
          ...doc,
          status: "Removed",
          statusVariant: "removed",
          contextText: "Removed in a later resubmission",
        });
        continue;
      }

      const replacingDoc = byId.get(supersededBy);
      historical.push({
        ...doc,
        status: "Replaced",
        statusVariant: "replaced",
        contextText: replacingDoc
          ? `Replaced by: ${replacingDoc.original_filename || replacingDoc.stored_filename}`
          : "Replaced in a later resubmission",
      });
    }

    const byCreatedDesc = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
    active.sort(byCreatedDesc);
    historical.sort(byCreatedDesc);

    return { active, historical };
  }, [selectedApp?.documents]);

  // ✅ NEW: filetype detection for preview
  const getFileType = (filename = "") => {
    const lower = String(filename).toLowerCase();
    if (lower.endsWith(".pdf")) return "pdf";
    if (lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".webp"))
      return "image";
    return "other";
  };

  const openDocPreview = (label, filename, fileUrl) => {
    if (!filename || !fileUrl) return;
    setDocPreview({ label, filename, url: fileUrl, type: getFileType(filename) });
  };

  const closeDocPreview = () => setDocPreview(null);

  /**
   * Render a file row with preview (Eye) and open (external link) buttons.
   * Now receives structured doc data from business_application_documents.
   */
  const renderFileLink = (
    applicationId,
    label,
    storedFilename,
    originalFilename,
    docId,
    options = {}
  ) => {
    if (!storedFilename || !docId || !applicationId) return null;

    const fileUrl = adminApi.getApplicationDocumentUrl(applicationId, docId);
    const {
      status = null,
      statusVariant = "current",
      contextText = null,
    } = options;

    const statusClass = {
      current: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      original: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
      replaced: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
      removed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    }[statusVariant] || "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";

    return (
      <div key={docId} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg mb-2">
        <div className="flex items-center min-w-0">
          <FileText className="w-5 h-5 mr-2 text-blue-500 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                {label}
              </span>
              {status ? (
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusClass}`}>
                  {status}
                </span>
              ) : null}
            </div>
            <span className="block text-xs text-gray-500 dark:text-gray-300 truncate">
              {originalFilename || storedFilename}
            </span>
            {contextText ? (
              <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate">
                {contextText}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Eye icon preview */}
          <button
            type="button"
            onClick={() => openDocPreview(label, storedFilename, fileUrl)}
            className="theme-icon-button"
            title={`Preview ${label}`}
          >
            <Eye className="w-4 h-4" />
          </button>

          {/* Open in new tab */}
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="theme-link inline-flex items-center"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4 mr-1" /> Open
          </a>
        </div>
      </div>
    );
  };

  const requestedAtText = useMemo(() => {
    return (app) => {
      const dt = app.created_at;
      try {
        return new Date(dt).toLocaleDateString();
      } catch {
        return "N/A";
      }
    };
  }, []);

  return (
    <div className="admin-data-panel admin-workspace-panel operations-surface overflow-hidden">
      <div className="theme-panel-divider p-6 border-b">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            Business Applications
            {counts.total > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">({counts.total} total)</span>
            )}
          </h2>
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <ShopSelect
              value={sortBy}
              onValueChange={setSortBy}
              options={[
                { value: "updated_at", label: "Sort: Last Updated" },
                { value: "created_at", label: "Sort: Date Created" },
                { value: "last_resubmitted", label: "Sort: Last Resubmitted" },
                { value: "resubmit_count", label: "Sort: Resubmit Count" },
              ]}
              ariaLabel="Sort business applications"
              align="start"
              minPanelWidth={192}
              triggerClassName="min-h-8 w-48 rounded-lg px-2.5 py-1.5 text-xs"
              panelClassName="py-1"
              optionClassName="text-xs"
            />
            <button
              onClick={() => setSortOrder(sortOrder === "DESC" ? "ASC" : "DESC")}
              className="theme-btn-neutral px-2 py-1.5 text-xs"
              title={`Sort ${sortOrder === "DESC" ? "Ascending" : "Descending"}`}
            >
              {sortOrder === "DESC" ? "↓ Newest" : "↑ Oldest"}
            </button>
            <Button
              onClick={refreshApplicationInventory}
              icon={RefreshCw}
              loading={loading}
              aria-label="Refresh business applications"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Filter chips row */}
        <div className="flex flex-wrap gap-2">
          {/* Status filters */}
          {[
            { key: "ALL", label: "All", count: counts.total },
            { key: "PENDING", label: "Pending", count: counts.byStatus?.PENDING || 0 },
            { key: "IN_REVIEW", label: "In Review", count: counts.byStatus?.IN_REVIEW || 0 },
            { key: "APPROVED", label: "Approved", count: counts.byStatus?.APPROVED || 0 },
            { key: "REJECTED", label: "Rejected", count: counts.byStatus?.REJECTED || 0 },
            { key: "CANCELLED", label: "Cancelled", count: counts.byStatus?.CANCELLED || 0 },
          ].map((chip) => (
            <button
              key={chip.key}
              onClick={() => { setStatusFilter(chip.key); setSpecialFilter(null); }}
              className={`theme-filter-pill inline-flex items-center gap-1.5 ${
                statusFilter === chip.key && !specialFilter
                  ? "theme-filter-pill-active"
                  : ""
              }`}
            >
              {chip.label}
              {chip.count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                  statusFilter === chip.key && !specialFilter
                    ? "bg-white/20 text-white"
                    : "bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300"
                }`}>
                  {chip.count}
                </span>
              )}
            </button>
          ))}

          <span className="mx-1 h-6 w-px self-center bg-neutral-300 dark:bg-neutral-700" />

          {/* Special filters */}
          {[
            { key: "resubmitted", label: "Resubmitted", count: counts.resubmitted },
            { key: "needs_info", label: "Needs Info", count: counts.needsInfo },
            { key: "high_resubmit", label: "High Resubmit (3+)", count: null },
          ].map((chip) => (
            <button
              key={chip.key}
              onClick={() => {
                setSpecialFilter(specialFilter === chip.key ? null : chip.key);
                setStatusFilter("ALL");
              }}
              className={`theme-filter-pill inline-flex items-center gap-1.5 ${
                specialFilter === chip.key
                  ? "theme-filter-pill-active"
                  : ""
              }`}
            >
              {chip.label}
              {chip.count != null && chip.count > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                  specialFilter === chip.key
                    ? "bg-white/20 text-white"
                    : "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-300"
                }`}>
                  {chip.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="operations-table-wrap">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-neutral-300 border-t-indigo-600 dark:border-neutral-700 dark:border-t-indigo-400"></div>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Loading applications...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500 dark:text-red-400">
            {error}
            <button
              onClick={fetchPendingBusinesses}
              className="ml-4 underline hover:text-red-700 dark:hover:text-red-300"
            >
              Retry
            </button>
          </div>
        ) : businesses.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No pending business applications found.
          </div>
        ) : (
          <table className="admin-data-table w-full text-left text-sm">
            <thead className="theme-table-head">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Business Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Owner
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Requested At
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {visibleBusinesses.map((app) => (
                <tr key={app.id} className="theme-table-row">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {app.business_name}
                    </div>
                    {app.registration_number && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">Reg: {app.registration_number}</div>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          {
                            APPROVED: 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
                            REJECTED: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
                            IN_REVIEW: 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
                          }[app.status] || 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                        }`}>
                          {app.status || 'PENDING'}
                        </span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-300">
                      {app.owner_first_name} {app.owner_last_name}
                    </div>
                    {app.username && (
                      <div className="text-xs text-gray-400">@{app.username}</div>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-300">
                      {app.owner_email}
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-300">
                      {requestedAtText(app)}
                    </div>
                    {(app.resubmit_count || 0) > 0 && (
                      <div className="mt-1 text-xs text-indigo-600 dark:text-indigo-400 font-semibold">
                        Resubmitted x{app.resubmit_count}
                      </div>
                    )}
                    {app.last_resubmitted_at && (
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        Last: {new Date(app.last_resubmitted_at).toLocaleString()}
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openReviewModal(app)}
                        className="theme-icon-button text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                        title="Review Application"
                      >
                        <Pencil size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminTablePagination page={page} totalPages={totalPages} totalItems={businesses.length} pageSize={PAGE_SIZE} onPageChange={setPage} />

      {/* Review Modal */}
      {selectedApp && (
        <div className="admin-modal-backdrop theme-modal-backdrop overflow-y-auto">
          <div className="admin-action-modal theme-modal-panel max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="admin-modal-header flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Review Application: {selectedApp.business_name}
              </h3>
              <button
                onClick={closeReviewModal}
                className="theme-icon-button"
              >
                <X size={24} />
              </button>
            </div>

            {detailLoading ? (
              <div className="admin-modal-body p-8 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-neutral-300 border-t-indigo-600 dark:border-neutral-700 dark:border-t-indigo-400"></div>
                <p className="mt-2 text-gray-500 dark:text-gray-400">Loading details…</p>
              </div>
            ) : (
            <div className="admin-modal-body p-6 space-y-6">
              {/* Owner Info */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  Owner Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
                    <p className="font-medium text-gray-900 dark:text-gray-200">
                      {selectedApp.owner_first_name} {selectedApp.owner_last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                    <p className="font-medium text-gray-900 dark:text-gray-200">
                      {selectedApp.owner_email}
                    </p>
                  </div>
                  {selectedApp.owner_phone && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                      <p className="font-medium text-gray-900 dark:text-gray-200">
                        {selectedApp.owner_phone}
                      </p>
                    </div>
                  )}
                  {selectedApp.username && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Username</p>
                      <p className="font-medium text-gray-900 dark:text-gray-200">
                        @{selectedApp.username}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Business Info */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  Business Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Business Name</p>
                    <p className="font-medium text-gray-900 dark:text-gray-200">
                      {selectedApp.business_name}
                    </p>
                  </div>
                  {selectedApp.registration_number && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Registration No.</p>
                      <p className="font-medium text-gray-900 dark:text-gray-200">
                        {selectedApp.registration_number}
                      </p>
                    </div>
                  )}
                  {selectedApp.tax_id && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Tax ID / TIN</p>
                      <p className="font-medium text-gray-900 dark:text-gray-200">
                        {selectedApp.tax_id}
                      </p>
                    </div>
                  )}
                  {selectedApp.business_email && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Business Email</p>
                      <p className="font-medium text-gray-900 dark:text-gray-200">
                        {selectedApp.business_email}
                      </p>
                    </div>
                  )}
                  {selectedApp.business_phone && (
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Business Phone</p>
                      <p className="font-medium text-gray-900 dark:text-gray-200">
                        {selectedApp.business_phone}
                      </p>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Address</p>
                    <p className="font-medium text-gray-900 dark:text-gray-200">
                      {[selectedApp.address_line1, selectedApp.address_line2, selectedApp.city, selectedApp.province, selectedApp.postal_code].filter(Boolean).join(", ")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedApp.notes && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Notes</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedApp.notes}</p>
                </div>
              )}

              {/* Documents */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  Documents
                </h4>
                {selectedApp.documents && selectedApp.documents.length > 0 ? (
                  <div className="space-y-3">
                    <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-2">
                      <p className="text-[11px] text-blue-800 dark:text-blue-300">
                        <span className="font-semibold">Tip:</span> Current files are at the top. Historical files are grouped separately below.
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                        Current Documents ({groupedDocuments.active.length})
                      </p>
                      {groupedDocuments.active.length > 0 ? (
                        <div className="space-y-2">
                          {groupedDocuments.active.map((doc) =>
                            renderFileLink(
                              selectedApp.id,
                              DOC_LABELS[doc.field_name] || doc.field_name,
                              doc.stored_filename,
                              doc.original_filename,
                              doc.id,
                              {
                                status: doc.status,
                                statusVariant: doc.statusVariant,
                                contextText: doc.contextText,
                              }
                            )
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic">No current active documents.</p>
                      )}
                    </div>

                    {groupedDocuments.historical.length > 0 ? (
                      <details className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                        <summary className="cursor-pointer text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                          Historical Documents ({groupedDocuments.historical.length})
                        </summary>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 mb-2">
                          These are older files kept for audit trail.
                        </p>
                        <div className="space-y-2">
                          {groupedDocuments.historical.map((doc) =>
                            renderFileLink(
                              selectedApp.id,
                              DOC_LABELS[doc.field_name] || doc.field_name,
                              doc.stored_filename,
                              doc.original_filename,
                              doc.id,
                              {
                                status: doc.status,
                                statusVariant: doc.statusVariant,
                                contextText: doc.contextText,
                              }
                            )
                          )}
                        </div>
                      </details>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic">No documents attached.</p>
                )}
              </div>

              {/* History */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                  Submission & Audit History
                </h4>

                <div className="space-y-3">
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Versions</p>
                    {history.versions.length ? (
                      <ul className="space-y-3">
                        {history.versions.map((v) => (
                          <li key={v.id} className="text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 pb-2 last:border-b-0">
                            <div className="flex items-center gap-2">
                              <GitCompare className="w-4 h-4 text-indigo-500 shrink-0" />
                              <span className="font-semibold">v{v.version_number}</span>
                              <span className="text-gray-500 dark:text-gray-400">— {new Date(v.created_at).toLocaleString()}</span>
                              <span className="text-gray-500 dark:text-gray-400">by {prettyActorName(v)}</span>
                            </div>
                            {v.notes ? <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 ml-6">Notes: {v.notes}</div> : null}
                            <div className="ml-6">
                              <VersionDiff
                                changedFields={v.changed_fields}
                                docChanges={docChangesByVersion[v.version_number]}
                                snapshot={v.snapshot}
                                versionDocs={versionDocsByVersion[v.version_number]}
                              />
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No resubmission versions yet.</p>
                    )}
                  </div>

                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Admin Actions Only</p>
                    {adminOnlyLogs.length ? (
                      <ul className="space-y-2">
                        {adminOnlyLogs.slice(0, 8).map((l) => (
                          <li key={l.id} className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-semibold">{prettyActorName(l)}: {ADMIN_ACTION_LABELS[l.action_type] || l.action_type}</span>
                            <span className="text-gray-500 dark:text-gray-400"> — {new Date(l.created_at).toLocaleString()}</span>
                            {l.description ? <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{l.description}</div> : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No admin-only actions found.</p>
                    )}
                  </div>

                  {/* Immutable Audit Event Timeline */}
                  {(history.events || []).length > 0 && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Application Event Timeline (Admin + Applicant)</p>
                      <div className="relative pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                        {history.events.map((ev) => (
                          <div key={ev.id} className="relative">
                            <span className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${EVENT_TYPE_COLORS[ev.event_type] || 'bg-gray-400'}`} />
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(ev.created_at).toLocaleString()}
                              <span className="ml-1">— {prettyActorName(ev)}</span>
                              {ev.actor_role && <span className="ml-1 text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500">({ev.actor_role})</span>}
                            </div>
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              <span className="font-semibold">{EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}</span>
                              {ev.summary && <span className="ml-1">— {ev.summary}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            <div className="admin-modal-footer theme-panel-footer flex justify-end space-x-3 p-6">
              <button
                onClick={closeReviewModal}
                className="theme-btn-neutral"
              >
                Close
              </button>

              {!['APPROVED', 'REJECTED', 'MORE_INFO_REQUIRED', 'CANCELLED'].includes(selectedApp.status) && (
                <>
                  <button
                    onClick={() => handleReject(selectedApp)}
                    className="theme-btn-danger"
                  >
                    Reject Application
                  </button>

                  <button
                    onClick={() => handleRequestInfo(selectedApp)}
                    className="theme-btn-primary"
                  >
                    Request More Info
                  </button>

                  <button
                    onClick={() => handleApprove(selectedApp)}
                    className="theme-btn-success"
                  >
                    Approve Application
                  </button>
                </>
              )}
            </div>

            {/* ✅ NEW: Document Preview Modal (inside Review Modal) */}
            {docPreview && (
              <div className="admin-modal-backdrop theme-modal-backdrop z-[60]">
                <div className="admin-document-modal theme-modal-panel max-w-3xl max-h-[90vh] overflow-hidden">
                  <div className="admin-modal-header flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500 dark:text-gray-300">Preview</p>
                      <h4 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                        {docPreview.label}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-300 truncate">
                        {docPreview.filename}
                      </p>
                    </div>

                    <button
                      onClick={closeDocPreview}
                      className="theme-icon-button"
                      title="Close Preview"
                    >
                      <X size={22} />
                    </button>
                  </div>

                  <div className="admin-document-body p-4 bg-gray-50 dark:bg-gray-900/40">
                    {docPreview.type === "pdf" ? (
                      <iframe
                        title="Document Preview"
                        src={docPreview.url}
                        className="w-full h-[70vh] rounded-lg border border-gray-200 dark:border-gray-700 bg-white"
                      />
                    ) : docPreview.type === "image" ? (
                      <div className="w-full flex justify-center">
                        <img
                          src={docPreview.url}
                          alt={docPreview.label}
                          className="max-h-[70vh] w-auto rounded-lg border border-gray-200 dark:border-gray-700"
                        />
                      </div>
                    ) : (
                      <div className="p-6 text-center text-gray-600 dark:text-gray-300">
                        <p className="font-medium">Preview not supported for this file type.</p>
                        <p className="text-sm mt-2">Use “Open” to view it in a new tab.</p>
                      </div>
                    )}
                  </div>

                  <div className="admin-modal-footer theme-panel-footer flex justify-end gap-2 p-4">
                    <a
                      href={docPreview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="theme-btn-primary"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" /> Open in new tab
                    </a>
                    <button
                      onClick={closeDocPreview}
                      className="theme-btn-neutral"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* ✅ END: Document Preview Modal */}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessApplications;
